/**
 * AlgorithmsPage - Browse and search all algorithms
 */
import React, { useState, useEffect } from 'react';
import { algorithmService } from '../services/api';
import AlgorithmCard from '../components/algorithm/AlgorithmCard';
import { useDebounce } from '../hooks';
import './AlgorithmsPage.css';

const DIFFICULTIES = ['All', 'Easy', 'Medium', 'Hard'];
const UNITS = ['All', 'Unit I', 'Unit II', 'Unit III', 'Unit IV', 'Unit V', 'Unit VI'];

function AlgorithmsPage() {
  const [algorithms, setAlgorithms] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState('');
  const [difficulty, setDifficulty] = useState('All');
  const [unit, setUnit] = useState('All');

  const debouncedSearch = useDebounce(search, 300);

  // Load all algorithms
  useEffect(() => {
    algorithmService.getAll()
      .then(res => {
        setAlgorithms(res.data || []);
        setFiltered(res.data || []);
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  // Filter whenever criteria changes
  useEffect(() => {
    let result = [...algorithms];

    if (debouncedSearch) {
      const q = debouncedSearch.toLowerCase();
      result = result.filter(a =>
        a.name.toLowerCase().includes(q) ||
        a.description.toLowerCase().includes(q) ||
        a.tags?.some(t => t.toLowerCase().includes(q))
      );
    }

    if (difficulty !== 'All') {
      result = result.filter(a => a.difficulty === difficulty);
    }

    if (unit !== 'All') {
      result = result.filter(a => a.unit === unit);
    }

    setFiltered(result);
  }, [debouncedSearch, difficulty, unit, algorithms]);

  const clearFilters = () => {
    setSearch('');
    setDifficulty('All');
    setUnit('All');
  };

  const hasFilters = search || difficulty !== 'All' || unit !== 'All';

  return (
    <div className="algorithms-page">
      <div className="container">
        {/* Header */}
        <div className="page-header">
          <h1>Algorithm Library</h1>
          <p>Explore {algorithms.length} OS algorithms across all 6 units with interactive visualizations</p>
        </div>

        {/* Search & Filters */}
        <div className="filter-bar">
          <div className="search-bar" style={{ flex: 1 }}>
            <span className="search-icon">🔍</span>
            <input
              type="text"
              placeholder="Search algorithms, topics, tags..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>

          <select
            className="filter-select"
            value={difficulty}
            onChange={e => setDifficulty(e.target.value)}
          >
            {DIFFICULTIES.map(d => (
              <option key={d} value={d}>{d === 'All' ? 'All Difficulties' : d}</option>
            ))}
          </select>

          <select
            className="filter-select"
            value={unit}
            onChange={e => setUnit(e.target.value)}
          >
            {UNITS.map(u => (
              <option key={u} value={u}>{u === 'All' ? 'All Units' : u}</option>
            ))}
          </select>

          {hasFilters && (
            <button className="btn btn-ghost btn-sm" onClick={clearFilters}>
              Clear ×
            </button>
          )}
        </div>

        {/* Results info */}
        {!loading && (
          <div className="results-info">
            <span>{filtered.length} algorithm{filtered.length !== 1 ? 's' : ''} found</span>
            {hasFilters && <span className="text-muted"> · filtered</span>}
          </div>
        )}

        {/* States */}
        {loading && (
          <div className="loader">
            <div className="spinner" />
          </div>
        )}

        {error && (
          <div className="error-state card">
            <p>⚠️ Failed to load algorithms: {error}</p>
            <p className="text-muted">Make sure the server is running on port 5000</p>
          </div>
        )}

        {!loading && !error && filtered.length === 0 && (
          <div className="empty-state card">
            <p style={{ fontSize: '2rem' }}>🔭</p>
            <p>No algorithms match your search.</p>
            <button className="btn btn-secondary btn-sm" onClick={clearFilters}>
              Clear Filters
            </button>
          </div>
        )}

        {!loading && !error && (
          <div className="algo-grid">
            {filtered.map(algo => (
              <AlgorithmCard key={algo.id} algorithm={algo} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default AlgorithmsPage;
